/**
 * Scroll reveal
 * -------------
 * Fades and slides each top-level section into view as it scrolls into the
 * viewport, similar to the reveal-on-scroll effect used on apple.com.
 *
 * Progressive enhancement: sections are only hidden once this script has
 * actually run (via the `scroll-reveal-ready` class on <html>), so nothing
 * disappears if the script fails to load or `prefers-reduced-motion` is set.
 */

const SECTION_SELECTOR = 'main#MainContent > .shopify-section';
const READY_CLASS = 'scroll-reveal-ready';
const SECTION_CLASS = 'scroll-reveal-section';
const VISIBLE_CLASS = 'scroll-reveal-visible';
const INSTANT_CLASS = 'scroll-reveal-instant';

// How much extra delay to stack on sections that enter the viewport at the
// same time (e.g. on first load), so they cascade in rather than popping
// together. Delay resets once a section has had time to reveal on its own.
const BASE_DELAY_MS = 60;
const STAGGER_STEP_MS = 90;
const STAGGER_MAX_STEPS = 4;
const STAGGER_RESET_MS = 400;

/** @type {IntersectionObserver | null} */
let observer = null;
let staggerIndex = 0;
let lastRevealAt = 0;

// Only the fade/rise-in animation should play while the visitor scrolls
// down the page. Scrolling back up just shows sections as-is, instantly.
let lastScrollY = window.scrollY;
let scrollDirection = 'down';

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function trackScrollDirection() {
  const currentScrollY = window.scrollY;

  if (currentScrollY > lastScrollY) scrollDirection = 'down';
  else if (currentScrollY < lastScrollY) scrollDirection = 'up';

  lastScrollY = currentScrollY;
}

/**
 * Shows a section immediately, with no transition. Used when a section
 * enters the viewport while scrolling up.
 * @param {HTMLElement} target
 */
function revealInstantly(target) {
  target.classList.add(INSTANT_CLASS);
  target.classList.add(VISIBLE_CLASS);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => target.classList.remove(INSTANT_CLASS));
  });
}

function getObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const target = /** @type {HTMLElement} */ (entry.target);

        if (!entry.isIntersecting) {
          // Reset so the section is ready to reveal again next time it scrolls into view.
          target.classList.remove(VISIBLE_CLASS);
          continue;
        }

        if (scrollDirection === 'up') {
          // Don't play the animation when scrolling back up, just show it.
          revealInstantly(target);
          continue;
        }

        const now = performance.now();

        if (now - lastRevealAt > STAGGER_RESET_MS) staggerIndex = 0;
        lastRevealAt = now;

        const step = staggerIndex % STAGGER_MAX_STEPS;
        target.style.setProperty('--scroll-reveal-delay', `${BASE_DELAY_MS + step * STAGGER_STEP_MS}ms`);
        staggerIndex += 1;

        target.classList.add(VISIBLE_CLASS);
      }
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    }
  );

  return observer;
}

/**
 * @param {Element | null} section
 */
function prepareSection(section) {
  if (!(section instanceof HTMLElement)) return;
  if (section.classList.contains(SECTION_CLASS)) return;
  // Let a merchant/section opt out explicitly if a section shouldn't animate.
  if (section.hasAttribute('data-scroll-reveal-disabled')) return;

  section.classList.add(SECTION_CLASS);
  getObserver().observe(section);
}

/**
 * @param {ParentNode} root
 */
function scan(root) {
  root.querySelectorAll(SECTION_SELECTOR).forEach(prepareSection);
}

function watchForNewSections(main) {
  if (!('MutationObserver' in window)) return;

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        if (node.matches(':scope > .shopify-section') || node.matches('.shopify-section')) {
          prepareSection(node);
        }

        scan(node);
      });
    }
  });

  mutationObserver.observe(main, { childList: true, subtree: true });
}

function init() {
  if (prefersReducedMotion()) return;

  const main = document.getElementById('MainContent');
  if (!main) return;

  document.documentElement.classList.add(READY_CLASS);

  lastScrollY = window.scrollY;
  window.addEventListener('scroll', trackScrollDirection, { passive: true });

  scan(main);
  watchForNewSections(main);

  // Re-run for sections the theme editor swaps in while customizing.
  document.addEventListener('shopify:section:load', (event) => {
    prepareSection(/** @type {Element} */ (event.target));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
